import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Eye,
  Download,
  Filter,
  X,
  Copy,
  CheckCircle2,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency } from '../../lib/currency';
import {
  getOrdersPaginated,
} from '../../services/admin/orders';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale/tr';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const statusLabels = {
  pending: 'Ödeme Bekleniyor',
  paid: 'Ödeme Alındı',
  shipped: 'Kargoya Verildi',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal',
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    startDate: '',
    endDate: '',
  });
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const searchInitialized = useRef(false);

  const fetchOrders = useCallback(async (reset = true, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const filterObj = {
        status: filters.status && filters.status !== 'all' ? filters.status : undefined,
        search: filters.search || undefined,
        startDate: filters.startDate ? new Date(filters.startDate) : undefined,
        endDate: filters.endDate ? new Date(filters.endDate) : undefined,
      };

      const targetPage = reset ? 1 : page;
      const result = await getOrdersPaginated(filterObj, 20, targetPage);

      if (reset) {
        setOrders(result.orders);
      } else {
        setOrders((prev) => [...prev, ...result.orders]);
      }

      setHasMore(result.hasMore);
      setPage(result.nextPage);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (!silent) {
        toast({
          title: 'Hata',
          description: 'Siparişler yüklenirken bir hata oluştu.',
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters.status, filters.search, filters.startDate, filters.endDate, page, toast]);

  useAdminAutoRefresh((silent) => fetchOrders(true, silent), {
    interval: 5000,
    deps: [filters.status, filters.startDate, filters.endDate],
  });

  useEffect(() => {
    if (!searchInitialized.current) {
      searchInitialized.current = true;
      return undefined;
    }
    const debounceTimer = setTimeout(() => {
      fetchOrders(true, false);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [filters.search, fetchOrders]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      search: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const copyOrderNumber = (orderNumber) => {
    navigator.clipboard.writeText(orderNumber);
    toast({
      title: 'Kopyalandı',
      description: 'Sipariş numarası kopyalandı.',
    });
  };

  const exportToCSV = () => {
    const headers = [
      'Sipariş No',
      'Tarih',
      'Müşteri Adı',
      'E-posta',
      'Telefon',
      'Ürün Sayısı',
      'Toplam Tutar',
      'Durum',
    ];

    const rows = orders.map((order) => {
      // Safely convert timestamp-like values to Date
      let orderDate;
      if (order.createdAt) {
        if (order.createdAt.toDate && typeof order.createdAt.toDate === 'function') {
          orderDate = order.createdAt.toDate();
        } else if (order.createdAt.seconds) {
          // Timestamp object
          orderDate = new Date(order.createdAt.seconds * 1000);
        } else {
          orderDate = new Date(order.createdAt);
        }
      }
      
      // Fallback to current date if invalid
      if (!orderDate || isNaN(orderDate.getTime())) {
        orderDate = new Date();
      }
      
      return [
        order.orderNumber || order.id,
        format(orderDate, 'dd.MM.yyyy HH:mm', { locale: tr }),
        order.customerInfo?.fullName || '',
        order.customerInfo?.email || '',
        order.customerInfo?.phone || '',
        order.items?.length || 0,
        (order.total || 0).toFixed(2),
        statusLabels[order.status] || order.status,
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gappaq-siparisler-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Başarılı',
      description: `${orders.length} sipariş CSV olarak indirildi.`,
    });
  };

  const hasActiveFilters =
    filters.status || filters.search || filters.startDate || filters.endDate;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">Sipariş Yönetimi</h1>
            <p className="text-gray-600 mt-1">Tüm siparişleri görüntüleyin ve yönetin</p>
          </div>
          {orders.length > 0 && (
            <Button onClick={exportToCSV} variant="outline">
              <Download size={18} className="mr-2" />
              CSV İndir
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="admin-filter-bar bg-white p-4 rounded-lg border space-y-4">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500" />
            <span className="font-medium">Filtreler</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium mb-1">Durum</label>
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium mb-1">Başlangıç Tarihi</label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium mb-1">Bitiş Tarihi</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium mb-1">Ara</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Sipariş no, e-posta, telefon..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={clearFilters}>
                <X size={16} className="mr-2" />
                Filtreleri Temizle
              </Button>
            </div>
          )}
        </div>

        {/* Orders Table */}
        {loading && orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 text-lg">Henüz sipariş yok</p>
            {hasActiveFilters && (
              <Button onClick={clearFilters} className="mt-4" variant="outline">
                Filtreleri Temizle
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sipariş No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tarih
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Müşteri
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ürün Sayısı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Toplam
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => {
                      // Safely convert timestamp-like values to Date
                      let orderDate;
                      if (order.createdAt) {
                        if (order.createdAt.toDate && typeof order.createdAt.toDate === 'function') {
                          orderDate = order.createdAt.toDate();
                        } else if (order.createdAt.seconds) {
                          // Timestamp object
                          orderDate = new Date(order.createdAt.seconds * 1000);
                        } else {
                          orderDate = new Date(order.createdAt);
                        }
                      }
                      
                      // Fallback to current date if invalid
                      if (!orderDate || isNaN(orderDate.getTime())) {
                        orderDate = new Date();
                      }
                      
                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {order.orderNumber || order.id}
                              </span>
                              <button
                                onClick={() => copyOrderNumber(order.orderNumber || order.id)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Kopyala"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {format(orderDate, 'dd.MM.yyyy', { locale: tr })}
                            <div className="text-xs text-gray-400">
                              {format(orderDate, 'HH:mm', { locale: tr })}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {order.customerInfo?.fullName || 'Bilinmiyor'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {order.customerInfo?.email || ''}
                            </div>
                            <div className="text-sm text-gray-500">
                              {order.customerInfo?.phone || ''}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {order.items?.length || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(order.total || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={statusColors[order.status] || 'bg-gray-100 text-gray-800'}>
                              {statusLabels[order.status] || order.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/orders/${order.id}`)}
                            >
                              <Eye size={16} className="mr-2" />
                              Detay
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center">
                <Button variant="outline" onClick={() => fetchOrders(false)}>
                  Daha Fazla Yükle
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default OrdersPage;
